import { GridFilterPanel, GridSlotProps } from '@mui/x-data-grid-pro';
import { useTheme } from '@mui/material/styles';

export default function CustomFilterPanel(props: GridSlotProps['filterPanel']) {
  const theme = useTheme();

  return (
    <GridFilterPanel
      {...props}
      sx={[
        {
          p: 2,
          pl: 1,
          backgroundColor:
            theme.palette.mode === 'light'
              ? theme.palette.background.paper
              : theme.palette.semantic.surface.bg2,
          border: '0.5px solid',
          borderColor: theme.palette.semantic.outline.light,
          borderRadius: '4px',

          // Default filter panel styling
          '& .MuiDataGrid-filterForm': {
            display: 'grid',
            alignItems: 'center',
            width: '100%',
          },

          // Adjustment based on number of added filters
          '& .MuiDataGrid-panelContent:has(.MuiDataGrid-filterForm:only-child) .MuiDataGrid-filterForm':
            {
              gridTemplateColumns:
                '1px minmax(203px, max-content) minmax(203px, max-content) minmax(203px, max-content) 40px',
            },
          '& .MuiDataGrid-panelContent:has(.MuiDataGrid-filterForm:not(:only-child)) .MuiDataGrid-filterForm':
            {
              gridTemplateColumns:
                '123px 203px minmax(203px, max-content) minmax(203px, max-content) 40px',
            },

          '&:has(.MuiDataGrid-panelContent .MuiDataGrid-filterForm:only-child)':
            {
              width: 'fit-content',
              minWidth: 750,
            },

          '&:has(.MuiDataGrid-panelContent:has(.MuiDataGrid-filterForm:not(:only-child)))':
            {
              width: 'fit-content',
              minWidth: 800,
            },

          // Input Fields: Logical Operator fields (AND/OR)
          '& .MuiDataGrid-filterFormLogicOperatorInput': {
            gridColumn: 1,
            minWidth: '123px',
            width: '100%',
          },

          // Input Fields: Column Selection fields
          '& .MuiDataGrid-filterFormColumnInput': {
            gridColumn: 2,
            minWidth: '203px',
            width: '100%',
          },

          // Input Fields: Operator fields
          '& .MuiDataGrid-filterFormOperatorInput': {
            gridColumn: 3,
            minWidth: '203px',
            width: '100%',
          },

          // Input Fields: Value fields
          '& .MuiDataGrid-filterFormValueInput': {
            gridColumn: 4,
            minWidth: '203px',
            width: '100%',
            '& .MuiSelect-root:not(.MuiBox-root:has(.MuiAutocomplete-root)), & .MuiTextField-root:not(.MuiBox-root .MuiTextField-root)':
              {
                width: '203px',
              },
            '& .MuiBox-root:has(.MuiPickersTextField-root)': {
              alignItems: 'center',
              top: '-5px',
            },
            '& .MuiBox-root:has(.MuiTextField-root)': {
              alignItems: 'center',
              top: '9px',
              '& .MuiInputBase-root': {
                mt: '6px',
              },
            },
            '& .MuiBox-root:has(.MuiAutocomplete-root)': {
              alignItems: 'center',
              top: '-4.5px',
              '& .MuiInputBase-root': {
                mt: '7px',
                '& input': {
                  paddingBottom: 0,
                },
              },
            },
          },

          // Input Fields: Delete/Remove fields button
          '& .MuiDataGrid-filterFormDeleteIcon': {
            gridColumn: 5,
            gridRow: 1,
            justifySelf: 'center',
            alignSelf: 'center',
            width: 32,
            height: 32,
            padding: 0,
            margin: 0,
          },

          '& .MuiDataGrid-filterForm .MuiInputBase-root': {
            height: 40,
            fontSize: 16,
          },

          '& .MuiDataGrid-filterForm .MuiInputBase-input': {
            fontSize: 16,
          },

          '& .MuiDataGrid-filterForm .MuiInputLabel-root': {
            fontSize: 14,
          },

          '& .MuiDataGrid-filterForm:first-of-type .MuiDataGrid-filterFormLogicOperatorInput':
            {
              visibility: 'hidden',
            },

          '& .MuiDataGrid-panelContent': {
            paddingTop: '8px',
          },

          // Filter Panel Footer
          '& .MuiDataGrid-panelFooter': {
            padding: '0px',
            borderTop: 'none',
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 1.5,
          },

          '& .MuiDataGrid-panelFooter .MuiButtonBase-root': {
            fontSize: '14px',
            lineHeight: 1.5,
            textTransform: 'none !important',
            borderRadius: '4px',
          },

          // Footer: Reset Button
          '& .MuiDataGrid-panelFooter > button:nth-of-type(2)': {
            order: 1,
            padding: '8px 16px',
            fontSize: '14px',
            border: `1px solid ${theme.palette.semantic.button.secondary.border}`,
            color: theme.palette.semantic.button.secondary.text,
            '&:hover': {
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
            },
          },

          // Footer: Reset Button (remove icon)
          '& .MuiDataGrid-panelFooter > button:nth-of-type(2) .MuiButton-startIcon':
            {
              display: 'none',
              mx: 0,
            },

          // Footer: Add Filter Button
          '& .MuiDataGrid-panelFooter > button:nth-of-type(1)': {
            order: 2,
            fontSize: '14px',
            padding: '8px 16px',
            backgroundColor: theme.palette.semantic.button.primary.background,
            color: theme.palette.semantic.button.primary.text,
            boxShadow: '0px 1px 4px 0px #0000001F',
            height: '100%',
            '&:hover': {
              backgroundColor: (theme) =>
                theme.palette.semantic.button.primary.hover,
            },
          },
        },
      ]}
    />
  );
}
